import { type ExamCenterProctorRequestStorageObject } from '@exam-center/graphql';
import { useEffect, useMemo, useState } from 'react';

import { getContentInfoFromSignedUrl } from './utils';

export type ProctorRequestStorageObject = Pick<
  ExamCenterProctorRequestStorageObject,
  'fileName' | 'key'
>;

export interface FileWithInfo {
  name: string;
  type: string;
  size: number;
  value: {
    url: string;
    name: string;
  };
}

export function useProctorRequestFileNames(
  proctorRequestStorageObject: ProctorRequestStorageObject[],
) {
  return useMemo(
    () => proctorRequestStorageObject?.map((file) => file.key.split('/').pop() ?? '') ?? [],
    [proctorRequestStorageObject],
  );
}

/**
 * Resolves each storage object to a downloadable file with its content type and size, using
 * signed URLs fetched by the caller (member or admin query).
 */
export function useProctorRequestFilesWithInfo(
  proctorRequestStorageObject: ProctorRequestStorageObject[],
  fileNames: string[],
  signedUrls: Record<string, string> | undefined,
) {
  const [files, setFiles] = useState<FileWithInfo[]>([]);

  useEffect(() => {
    if (!proctorRequestStorageObject || !fileNames) {
      setFiles([]);
      return;
    }
    const getFiles = async () => {
      const files = await Promise.all(
        proctorRequestStorageObject.map(async (file, idx) => {
          const fileName = fileNames[idx];
          const url = signedUrls?.[fileName];
          // No signed URL yet (query still loading): skip the HEAD request instead of fetching
          // an "undefined" URL.
          const { type, size } = url
            ? await getContentInfoFromSignedUrl(url)
            : { type: '', size: 0 };
          return {
            name: file.fileName ?? '-',
            size: size,
            type,
            value: {
              url: url ?? '',
              name: fileName ?? '',
            },
          };
        }),
      );
      setFiles(files);
    };
    getFiles();
  }, [proctorRequestStorageObject, fileNames, signedUrls]);

  return files;
}
